import { useCallback, useEffect, useRef, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { Construction, ChevronLeft, ChevronRight } from 'lucide-react';
import { Container, PageHeader } from '@vnetwork/ui/layout';
import { IconButton } from '@vnetwork/ui/primitives';
import { WaapEmptyCard } from '../../WaapEmptyCard';
import {
  ADVANCED_TYPE_ANTI_LEECH,
  ADVANCED_TYPE_BRUTE_FORCE,
  ADVANCED_TYPE_CC_PROTECTION,
  ADVANCED_TYPE_XML_PROTECTION,
  ADVANCED_TYPE_VULNERABILITY_SCANNING,
  ADVANCED_TYPE_HONEYPOT,
  ADVANCED_TYPE_COOKIE,
  ADVANCED_TYPE_ILLEGAL_DOWNLOAD,
} from '@/api/waap';
import { WaapConsoleBreadcrumbs } from '@/pages/services/waap/WaapConsoleBreadcrumbs';
import { AdvancedStrategyPanel } from '@/pages/services/waap/aiwaf/advanced-protection/AdvancedStrategyPanel';
import { PageDefacementPanel } from '@/pages/services/waap/aiwaf/advanced-protection/PageDefacementPanel';
import { CsrfPanel } from '@/pages/services/waap/aiwaf/advanced-protection/CsrfPanel';
import { WeakPasswordPanel } from '@/pages/services/waap/aiwaf/advanced-protection/WeakPasswordPanel';
import { ADVANCED_TAB_SLUGS, advancedTabIndex } from './advanced-tabs';
import styles from './AdvancedProtectionTab.module.css';

/** The Advanced-Protection feature tabs, mirroring the vendor's tab bar. Wired
 *  tabs render their panel; the rest are placeholders pending their contracts. */
const ADVANCED_TABS = [
  'Anti-Leech',
  'Page Defacement Mitigation',
  'CSRF Protection',
  'Brute Force Protection',
  'CC Protection',
  'XML Protection',
  'Weak Password Validation',
  'Vulnerability Scanning',
  'Honeypot Protection',
  'Cookie Protection',
  'Illegal Download Protection',
] as const;

/**
 * AI-WAF → Advanced Protection. Account-level page (the `:id` domain only resolves
 * the customer session). A horizontal tab bar selects the protection feature.
 * Wired so far: Anti-Leech + Brute Force (waf_advanced_strategy types 1/2),
 * Page Defacement + CSRF (per-site config). Anchored on any one website.
 */
export function AdvancedProtectionTab() {
  const { serviceId } = useParams<{ serviceId: string }>();
  const base = `/services/waap/${serviceId}`;
  // Keep the active feature in the URL so returning from a rule form (which
  // remounts this tab) restores the feature the user was configuring rather
  // than resetting to the first tab.
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = advancedTabIndex(searchParams.get('tab'));
  const setActiveTab = (i: number) =>
    setSearchParams({ tab: ADVANCED_TAB_SLUGS[i] }, { replace: true });

  const sid = serviceId ?? '';

  const renderPanel = () => {
    switch (activeTab) {
      case 0:
        return (
          <AdvancedStrategyPanel
            key="anti-leech"
            serviceId={sid}
            base={base}
            type={ADVANCED_TYPE_ANTI_LEECH}
            title="Anti-Leech Rules"
            description="Anti-leech prevents untrusted third-party websites from hotlinking your static resources."
            addLabel="Add Anti-Leech Rule"
            formSegment="anti-leech"
            noun="Anti-Leech rule"
          />
        );
      case 1:
        return <PageDefacementPanel serviceId={sid} base={base} />;
      case 2:
        return <CsrfPanel serviceId={sid} base={base} />;
      case 3:
        return (
          <AdvancedStrategyPanel
            key="brute-force"
            serviceId={sid}
            base={base}
            type={ADVANCED_TYPE_BRUTE_FORCE}
            title="Brute Force Rules"
            description="Brute force protection guards websites against cracking attacks that try vast numbers of username/password combinations."
            addLabel="Add Brute Force Rule"
            formSegment="brute-force"
            noun="Brute Force rule"
          />
        );
      case 4:
        return (
          <AdvancedStrategyPanel
            key="cc-protection"
            serviceId={sid}
            base={base}
            type={ADVANCED_TYPE_CC_PROTECTION}
            title="CC Protection Rules"
            description="CC protection guards a website against surges of requests from the same source to specific paths."
            addLabel="Add CC Protection Rule"
            formSegment="cc-protection"
            noun="CC Protection rule"
          />
        );
      case 5:
        return (
          <AdvancedStrategyPanel
            key="xml-protection"
            serviceId={sid}
            base={base}
            type={ADVANCED_TYPE_XML_PROTECTION}
            title="XML Protection Rules"
            description="XML protection verifies XML requests to prevent attacks against servers."
            addLabel="Add XML Protection Rule"
            formSegment="xml-protection"
            noun="XML Protection rule"
          />
        );
      case 6:
        return <WeakPasswordPanel serviceId={sid} base={base} />;
      case 7:
        return (
          <AdvancedStrategyPanel
            key="vulnerability-scanning"
            serviceId={sid}
            base={base}
            type={ADVANCED_TYPE_VULNERABILITY_SCANNING}
            title="Vulnerability Scanning Rules"
            description="Vulnerability scanning detects and handles automated scanners probing your websites for weaknesses."
            addLabel="Add Vulnerability Scanning Rule"
            formSegment="vulnerability-scanning"
            noun="Vulnerability Scanning rule"
          />
        );
      case 8:
        return (
          <AdvancedStrategyPanel
            key="honeypot"
            serviceId={sid}
            base={base}
            type={ADVANCED_TYPE_HONEYPOT}
            title="Honeypot Protection Rules"
            description="Honeypot protection traps scanners with bait paths and serves them a configured response."
            addLabel="Add Honeypot Protection Rule"
            formSegment="honeypot"
            noun="Honeypot Protection rule"
          />
        );
      case 9:
        return (
          <AdvancedStrategyPanel
            key="cookie"
            serviceId={sid}
            base={base}
            type={ADVANCED_TYPE_COOKIE}
            title="Cookie Protection Rules"
            description="Cookie protection signs or encrypts cookies and enforces their security attributes."
            addLabel="Add Cookie Protection Rule"
            formSegment="cookie"
            noun="Cookie Protection rule"
          />
        );
      case 10:
        return (
          <AdvancedStrategyPanel
            key="illegal-download"
            serviceId={sid}
            base={base}
            type={ADVANCED_TYPE_ILLEGAL_DOWNLOAD}
            title="Illegal Download Protection Rules"
            description="Illegal download protection detects and protects the download of specific files."
            addLabel="Add an Illegal Download Protection Rule"
            formSegment="illegal-download"
            noun="Illegal Download rule"
          />
        );
      default:
        return (
          <WaapEmptyCard
            icon={<Construction size={20} />}
            title={`${ADVANCED_TABS[activeTab]} — coming soon`}
            description="This Advanced-Protection module isn't wired yet."
          />
        );
    }
  };

  return (
    <Container size="xl">
      <PageHeader
        breadcrumbs={
          <WaapConsoleBreadcrumbs trail={[{ label: 'AI-WAF' }, { label: 'Advanced Protection' }]} />
        }
        title="Advanced Protection"
        description="Targeted protection modules that go beyond the baseline WAF policy — each addresses a specific class of web threat."
      />

      <TabStrip tabs={ADVANCED_TABS} active={activeTab} onSelect={setActiveTab} />

      {renderPanel()}
    </Container>
  );
}

/**
 * A single-line, horizontally scrollable tab bar. When the tabs overflow, edge
 * arrow buttons appear to scroll left/right (and auto-hide at each end). The
 * native scrollbar is suppressed so the strip reads as a clean "swipe" rail.
 */
function TabStrip({
  tabs,
  active,
  onSelect,
}: {
  tabs: readonly string[];
  active: number;
  onSelect: (i: number) => void;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const [edges, setEdges] = useState({ left: false, right: false });

  const update = useCallback(() => {
    const el = ref.current;
    if (!el) return;
    const { scrollLeft, scrollWidth, clientWidth } = el;
    setEdges({ left: scrollLeft > 1, right: scrollLeft + clientWidth < scrollWidth - 1 });
  }, []);

  useEffect(() => {
    update();
    const el = ref.current;
    if (!el) return;
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, [update]);

  // Keep the active tab in view (e.g. on programmatic selection).
  useEffect(() => {
    ref.current?.querySelectorAll<HTMLElement>('[data-tab]')[active]?.scrollIntoView({
      behavior: 'smooth',
      inline: 'nearest',
      block: 'nearest',
    });
  }, [active]);

  const nudge = (dir: number) => ref.current?.scrollBy({ left: dir * 260, behavior: 'smooth' });

  return (
    <div className={styles.tabBar}>
      <style>{'.vn-tabstrip::-webkit-scrollbar{display:none}'}</style>
      {edges.left && (
        <IconButton aria-label="Scroll tabs left" variant="ghost" size="sm" onClick={() => nudge(-1)}>
          <ChevronLeft size={16} />
        </IconButton>
      )}
      <div
        ref={ref}
        className={`vn-tabstrip ${styles.tabScroll}`}
        onScroll={update}
      >
        {tabs.map((label, i) => {
          const isActive = i === active;
          return (
            <button
              key={label}
              data-tab=""
              onClick={() => onSelect(i)}
              className={styles.tab}
              style={{
                fontWeight: isActive ? 'var(--vn-font-weight-semibold)' : 'var(--vn-font-weight-regular)',
                color: isActive ? 'var(--vn-primary-text)' : 'var(--vn-fg-muted)',
                borderBottom: isActive
                  ? '2px solid var(--vn-primary-solid)'
                  : '2px solid transparent',
              }}
            >
              {label}
            </button>
          );
        })}
      </div>
      {edges.right && (
        <IconButton aria-label="Scroll tabs right" variant="ghost" size="sm" onClick={() => nudge(1)}>
          <ChevronRight size={16} />
        </IconButton>
      )}
    </div>
  );
}
